<template>
  <li class="relative" :style="{ '--menu-color': group.color }">
    <!-- Leaf group: navigates directly -->
    <router-link
      v-if="!hasItems"
      :to="group.path"
      :title="collapsed ? t(group.label) : undefined"
      :aria-current="isLeafActive ? 'page' : undefined"
      :class="[rowClass(isLeafActive), collapsed ? 'lg:justify-center' : '']"
      @click="emit('navigate')"
    >
      <Icon :icon="group.icon" width="22" height="22" class="shrink-0" />
      <span class="select-none" :class="collapsed ? 'lg:hidden' : ''">{{ t(group.label) }}</span>
      <span
        v-if="isLeafActive"
        aria-hidden="true"
        class="absolute start-0 inset-y-1.5 w-[3px] rounded-full bg-(--menu-color)"
      ></span>
    </router-link>

    <!-- Accordion group -->
    <button
      v-else
      type="button"
      :aria-expanded="collapsed ? false : open"
      :aria-controls="subListId"
      :title="collapsed ? t(group.label) : undefined"
      :class="[rowClass(false), collapsed ? 'lg:justify-center' : '']"
      @click="emit('toggle', group)"
    >
      <Icon :icon="group.icon" width="22" height="22" class="shrink-0" />
      <span class="select-none" :class="[containsActive ? 'text-font-dark font-medium' : '', collapsed ? 'lg:hidden' : '']">
        {{ t(group.label) }}
      </span>
      <Icon
        icon="tabler:chevron-down"
        width="16"
        height="16"
        class="ms-auto shrink-0 motion-safe:transition-transform motion-safe:duration-fast"
        :class="[open ? 'rotate-180' : '', collapsed ? 'lg:hidden' : '']"
      />
    </button>

    <!-- Sub items (grid 0fr -> 1fr accordion) -->
    <div
      v-if="hasItems"
      class="grid motion-safe:transition-[grid-template-rows] motion-safe:duration-normal motion-safe:ease-out"
      :class="open && !collapsed ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'"
    >
      <div class="overflow-hidden">
        <ul :id="subListId" class="p-1 ps-6 flex flex-col gap-2">
          <li
            v-for="item in group.items"
            :key="item.id"
            class="relative"
            :style="{ '--menu-color': item.color }"
          >
            <span
              v-if="item.path === activeItemPath"
              aria-hidden="true"
              class="absolute start-0 inset-y-1.5 w-[3px] rounded-full bg-(--menu-color)"
            ></span>
            <router-link
              :to="item.path"
              :aria-current="item.path === activeItemPath ? 'page' : undefined"
              :class="subRowClass(item.path === activeItemPath)"
              @click="emit('navigate')"
            >
              <span class="select-none">{{ t(item.label) }}</span>
            </router-link>
          </li>
        </ul>
      </div>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import type { MenuGroup } from '@/types/sidebar.d'

// props --------------------
const props = defineProps<{
  group: MenuGroup
  /** Accordion open state (owned by parent) */
  open: boolean
  /** Desktop rail (icon-only) mode */
  collapsed: boolean
  activeItemPath: string | null
}>()

// emits --------------------
const emit = defineEmits<{
  (e: 'toggle', group: MenuGroup): void
  (e: 'navigate'): void
}>()

const { t } = useI18n()

// computed --------------------
const hasItems = computed(() => (props.group.items?.length ?? 0) > 0)
const containsActive = computed(
  () => props.group.items?.some((item) => item.path === props.activeItemPath) ?? false,
)
const isLeafActive = computed(() => !hasItems.value && props.group.path === props.activeItemPath)
const subListId = computed(() => `sidebar-sub-${props.group.id}`)

// Shared row styling: neutral by default, group color only on the active row
const rowClass = (active: boolean) => [
  'flex w-full items-center gap-2.5 rounded-lg h-10 px-3 text-start text-body cursor-pointer whitespace-nowrap',
  'motion-safe:transition-colors motion-safe:duration-fast',
  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-font-gray focus-visible:ring-offset-2 focus-visible:ring-offset-form-box',
  active
    ? 'bg-[color:color-mix(in_srgb,var(--menu-color)_8%,transparent)] text-(--menu-color) font-medium'
    : 'text-font-gray hover:bg-bg-input hover:text-font-dark',
]

const subRowClass = (active: boolean) => [
  'flex w-full items-center h-9 ps-3 pe-2 rounded-lg text-body cursor-pointer whitespace-nowrap',
  'motion-safe:transition-colors motion-safe:duration-fast',
  'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-font-gray focus-visible:ring-offset-2 focus-visible:ring-offset-form-box',
  active
    ? 'bg-[color:color-mix(in_srgb,var(--menu-color)_8%,transparent)] text-(--menu-color) font-medium'
    : 'text-font-gray hover:bg-bg-input hover:text-font-dark',
]
</script>
